<script setup>
import { userAuthState } from '@/@core/auth'
import AnalyticsDepartmentCapaRating from '@/@core/components/feedback/AnalyticsDepartmentCapaRating.vue'
import AnalyticsDepartmentPSat from '@/@core/components/feedback/AnalyticsDepartmentPSat.vue'
import AnalyticsEpisodeDoctorWiseFeedback from '@/@core/components/feedback/AnalyticsEpisodeDoctorWiseFeedback.vue'
import AnalyticsExperienceBreakdown from '@/@core/components/feedback/AnalyticsExperienceBreakdown.vue'
import AnalyticsFormTypeTracker from '@/@core/components/feedback/AnalyticsFormTypeTracker.vue'
import AnalyticsGoogleReviewTracker from '@/@core/components/feedback/AnalyticsGoogleReviewTracker.vue'
import AnalyticsNPSBreakdown from '@/@core/components/feedback/AnalyticsNPSBreakdown.vue'
import AnalyticsNpsMonthlyTrend from '@/@core/components/feedback/AnalyticsNpsMonthlyTrend.vue'
import AnalyticsSourceTypeTracker from '@/@core/components/feedback/AnalyticsSourceTypeTracker.vue'
import AnalyticsUserWiseFeedback from '@/@core/components/feedback/AnalyticsUserWiseFeedback.vue'
import DashboardAnalyticsFeedbackTracker from '@/@core/components/feedback/DashboardAnalyticsFeedbackTracker.vue'
import DepartmentMonthlyAnalytics from '@/@core/components/feedback/DepartmentMonthlyAnalytics.vue'
import DepartmentWiseRating from '@/@core/components/feedback/DepartmentWiseRating.vue'
import FormNPSTracker from '@/@core/components/feedback/FormNPSTracker.vue'
import NPSMonthlyAnalysis from '@/@core/components/feedback/NPSMonthlyAnalysis.vue'
import { endOfMonth, startOfMonth } from '@/@core/utils/formatters'
import { can } from '@/@layouts/plugins/casl'
import { cookieRef } from '@/@layouts/stores/config'
import DashboardFeedbacksCard from '@/@core/components/feedback/DashboardFeedbacksCard.vue'
import DashboardInvitationsCard from '@/@core/components/feedback/DashboardInvitationsCard.vue'
import DashboardOutstandingCard from '@/@core/components/feedback/DashboardOutstandingCard.vue'
import DashboardResolvedCard from '@/@core/components/feedback/DashboardResolvedCard.vue'
import DashboardTicketTypeCard from '@/@core/components/feedback/DashboardTicketTypeCard.vue'
import FeedbackTable from '@/views/feedback/feedback-panel/FeedbackTable.vue'
import TicketTable from '@/views/feedback/ticket-management/TicketTable.vue'
import { computed } from 'vue'
import { useTenant } from '@/composables/useTenant'

const { isTenant } = useTenant()

definePage({
  meta: {
    name: 'dashboard',
    subject: 'feedback.menu',
    action: 'dashboard',
  },  
})


const auth = userAuthState()
const canAccessUnit = can('read', 'master.unit')

const {
  data: unitsData,
} =  await useApi(createUrl('/masters/units', {
  query: {
    paged: 1,
    select: ["id", "name"],
  },
}), {
  beforeFetch: ({ cancel }) =>{
    if(!canAccessUnit)
    {
      cancel()
    }
  },
})

const units = computed(() => {
  if (unitsData.value?.data) {
    return unitsData.value?.data
  }
  
  return [ {
    id: auth?.user?.unit_id,
    name: auth?.user?.unit_name,
  }]
})
 

const unit_id = ref(cookieRef('selectedUnit') ?? null)

const addDays = (date, days) => {
  const result = new Date(date)

  result.setDate(result.getDate() + days)
  
  return result
}


const addMonths = (date, months) => {
  const result = new Date(date)

  result.setMonth(result.getMonth() + months)
  
  return result
}

const customDateFormat = date => {
  return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`
}

const quickDateRangeOptions =[{
  title: 'Today',
  value: `${customDateFormat(new Date())}`,
},
{
  title: 'Yesterday',
  value: `${customDateFormat(addDays(new Date(), -1))}`,
},
{
  title: 'Last 7 days',
  value: `${customDateFormat(addDays(new Date(), -6))} to ${customDateFormat(new Date())}`,
},
{
  title: 'Last 30 days',
  value: `${customDateFormat(addDays(new Date(), -30))} to ${customDateFormat(new Date())}`,
},
{
  title: 'This month',
  value: `${customDateFormat(startOfMonth(new Date()))} to ${customDateFormat(new Date())}`,
},
{
  title: 'Last month',
  value: `${customDateFormat(startOfMonth(addMonths(new Date(), -1)))} to ${customDateFormat(endOfMonth(addMonths(new Date(), -1)))}`,
},
{
  title: 'Custom',
  value: null,
}]


const setQuickDateRange = value => {
 
  dateRange.value = value?.value
}

const quickDateRange = ref('Last 30 days')

const dateRange = ref()

const fromDate = ref(customDateFormat(addDays(new Date(), -30)))
const toDate = ref(customDateFormat(new Date()))

const dateRangeOptions = ref({
  mode: 'range',
  maxDate: new Date(),
  dateFormat: 'Y-m-d',
  enableTime: false,
  enableSeconds: false,
  noCalendar: false,
  altInput: true,
  altFormat: 'F j, Y',
  altInputClass: 'form-control',
  altInputPlaceholder: 'Select date',
  inline: false,
  static: false,
  appendTo: document.body,
  clickOpens: true,
  onChange: (selectedDates, dateStr, instance) => {
   
    fromDate.value = customDateFormat(selectedDates[0])
    if (selectedDates[1])
      toDate.value = customDateFormat(selectedDates[1])
    else{
      toDate.value = customDateFormat(selectedDates[0])
    }

    dateRange.value = dateStr
  },
  defaultDate: [customDateFormat(addDays(new Date(), -30)), customDateFormat(new Date())],
})

watch( dateRange, value => {
  if (value) {
    quickDateRange.value = quickDateRangeOptions.find(option => option.value === value)?.title || 'Custom'
  }
}, { immediate: true })

const dateRangeValue = computed(() => {
  return [
    fromDate.value,
    toDate.value,
  ]
})

const { data: totalFeedbackData } = useApi(createUrl('/dashboard/feedbacks/total-count', {
  query: {
    search: {
      created_at: dateRangeValue,
      unit_id: unit_id,
    },
  },
}))

const { data: totalFeedbackInviteData } = useApi(createUrl('/dashboard/feedbacks/invitation', {
  query: {
    search: {
      created_at: dateRangeValue,
      unit_id: unit_id,
    },
  },
}))

const { data: totalFeedbackTypeData } = useApi(createUrl('/dashboard/feedbacks/status', {
  query: {
    search: {
      created_at: dateRangeValue,
      unit_id: unit_id,
    },
  },
}))

const { data: totalTicketPendingData } = useApi(createUrl('/dashboard/tickets/status', {
  query: {
    search: {
      created_at: dateRangeValue,
      unit_id: unit_id,
      status: [
        "PENDING",
        "SNOOZE",
        "RE_ASSIGN",
        "RE_OPEN",
        "UNATTENDED",
        "IN_PROGRESS",
      ],
      ticket_type: [
        "COMPLAINT",
      ],
    },
  },
}))

const { data: totalTicketResolvedData } = useApi(createUrl('/dashboard/tickets/status', {
  query: {
    search: {
      created_at: dateRangeValue,
      unit_id: unit_id,
      status: [
        "RESOLVED",
        "DISPOSED",
      ],
      ticket_type: [
        "COMPLAINT",
      ],
    },
  },
}))

const { data: totalTicketTypeData } = useApi(createUrl('/dashboard/tickets/ticket-type', {
  query: {
    search: {
      created_at: dateRangeValue,
      unit_id: unit_id,
    },
  },
}))

// Feedback Filter
const feedbackTableFilter = ref(null)
const isFeedbackTableDialogVisible = ref(false)

const onUpdateFeedbackTableFilter = data => {
  feedbackTableFilter.value = {
    created_at: [fromDate.value, toDate.value],
    unit_id: unit_id.value,
    ...data,
  }
  isFeedbackTableDialogVisible.value = true
}

watch(() => isFeedbackTableDialogVisible.value, value => {
  if (value === false) feedbackTableFilter.value = null
})

// Ticket Filter
const ticketTableFilter = ref(null)
const ticketType = ref(null)
const isTicketTableDialogVisible = ref(false)

const onUpdateTicketTableFilter = data => {
  ticketType.value = data.types[0] ?? null

  const { types, ...rest } = data

  ticketTableFilter.value = {
    created_at: [fromDate.value, toDate.value],
    unit_id: unit_id.value,

    // status: [],
    // ticket_type: [
    //   "COMPLAINT",
    //   "COMPLIMENT",
    //   "SUGGESTION",
    //   "SPECIAL_REQUEST",
    // ],
    ...rest, 
  }
  isTicketTableDialogVisible.value = true
}

watch(() => isTicketTableDialogVisible.value, value => {
  if (value === false) ticketTableFilter.value = null
})

// const { data: totalFeedbackGoogleReviewed } = useApi(createUrl('/dashboard/feedbacks/google-review/monthly', {
//   query: {
//     search: {
//       created_at: dateRangeValue,
//       unit_id: unit_id,
//     },
//   },
// }))
</script>

<template>
  <div>
    <div class="text-h4 mb-6">
      Feedback Dashboard
    </div>

    <VCard
      variant="flat"
      class="border mb-6"
    >
      <VCardText>
        <!--
          <span class="text-h6 font-weight-medium">
          Filter By Date
          </span> 
        -->
        <VRow align="center">
          <VCol
            cols="12"
            md="6"
            sm="6"
          >
            <AppSelect
              v-model="quickDateRange"
              label="Quick Range"
              return-object
              :item-title="item => item.title"
              :item-value="item => item.title"
              :items="quickDateRangeOptions"
              @update:model-value="setQuickDateRange"
            />
          </VCol>
          <VCol
            cols="12"
            md="6"
            sm="6"
          >
            <AppDateTimePicker
              v-model="dateRange"
              label="Range"
              placeholder="Select date"
              :config="dateRangeOptions"
            />
          </VCol>
          <!--
            <VCol
            v-if="units.length > 1"
            cols="12"
            md="4"
            sm="6"
            >
            <AppSelect
            v-model="unit_id"
            clearable
            multiple
            label="Select Unit"
            placeholder="Select Unit"
            :items="units"
            :item-title="item => item.name"
            :item-value="item => item.id"
            />
            </VCol> 
          -->
        </VRow>
      </VCardText>
    </VCard>

    <!-- Analytics Cards -->
    <VRow class="match-height">
      <!-- Row 1: Feedbacks + Invitations -->
      <VCol
        v-if="can('view', 'dashboard.widget.total_feedbacks') || can('view', 'dashboard.widget.feedbacks_by_status')"
        cols="12"
        md="8"
        style="animation-delay: 0ms;"
      >
        <DashboardFeedbacksCard
          :total-data="totalFeedbackData"
          :status-data="totalFeedbackTypeData"
        />
      </VCol>
      <VCol
        v-if="can('view', 'dashboard.widget.feedback_invitation_sent')"
        cols="12"
        md="4"
        style="animation-delay: 70ms;"
      >
        <DashboardInvitationsCard :data="totalFeedbackInviteData" />
      </VCol>

      <!-- Row 2: Tickets + Outstanding + Resolved -->
      <VCol
        v-if="can('view', 'dashboard.widget.tickets_by_type')"
        cols="12"
        md="4"
        style="animation-delay: 140ms;"
      >
        <DashboardTicketTypeCard :data="totalTicketTypeData" />
      </VCol>
      <VCol
        v-if="can('view', 'dashboard.widget.outstanding_complaints')"
        cols="12"
        md="4"
        style="animation-delay: 210ms;"
      >
        <DashboardOutstandingCard :data="totalTicketPendingData" />
      </VCol>
      <VCol
        v-if="can('view', 'dashboard.widget.resolved_complaints')"
        cols="12"
        md="4"
        style="animation-delay: 280ms;"
      >
        <DashboardResolvedCard
          :data="totalTicketResolvedData"
          :outstanding-total="totalTicketPendingData?.total_ticket_count ?? 0"
        />
      </VCol>
      <!-- Sarvodaya: Show NPS + Experience earlier (before Department P-Sat) -->
      <template v-if="isTenant('sarvodaya')">
        <VCol
          v-if="can('view', 'dashboard.widget.nps_score')"
          cols="12"
          lg="6"
          md="6"
        >
          <AppLazy :key="unit_id + fromDate + toDate">
            <AnalyticsNPSBreakdown
              title="NPS Score"
              :from="fromDate"
              :to="toDate"
              :unit-id="unit_id"
              @update:feedback-table-filter="onUpdateFeedbackTableFilter"
            />
          </AppLazy>
        </VCol>
        <VCol
          v-if="can('view', 'dashboard.widget.experience_breakdown')"
          cols="12"
          lg="6"
          md="6"
        >
          <AppLazy :key="unit_id + fromDate + toDate">
            <AnalyticsExperienceBreakdown
              title="Experience Breakdown"
              :from="fromDate"
              :to="toDate"
              :unit-id="unit_id"
              @update:feedback-table-filter="onUpdateFeedbackTableFilter"
            />
          </AppLazy>
        </VCol>
      </template>

      <!-- Department P-Sat -->
      <VCol
        v-if="can('view', 'dashboard.widget.department_p_sat')"
        cols="12"
      >
        <AppLazy :key="unit_id + fromDate + toDate">
          <AnalyticsDepartmentPSat
            :from="fromDate"
            :to="toDate"
            :unit-id="unit_id"
          />
        </AppLazy>
      </VCol>

      <!-- NPS Score -->
      <VCol
        v-if="!isTenant('sarvodaya') && can('view', 'dashboard.widget.nps_score')"
        cols="12"
        lg="6"
        md="6"
      >
        <AppLazy :key="unit_id + fromDate + toDate">
          <AnalyticsNPSBreakdown
            title="NPS Score"
            :from="fromDate"
            :to="toDate"
            :unit-id="unit_id"
            @update:feedback-table-filter="onUpdateFeedbackTableFilter"
          />
        </AppLazy>
      </VCol>

      <!-- Experience Breakdown -->
      <VCol
        v-if="!isTenant('sarvodaya') && can('view', 'dashboard.widget.experience_breakdown')"
        cols="12"
        lg="6"
        md="6"
      >
        <AppLazy :key="unit_id + fromDate + toDate">
          <AnalyticsExperienceBreakdown
            title="Experience Breakdown"
            :from="fromDate"
            :to="toDate"
            :unit-id="unit_id"
            @update:feedback-table-filter="onUpdateFeedbackTableFilter"
          />
        </AppLazy>
      </VCol>

      <!-- Department Wise Rating -->
      <VCol
        v-if="can('view', 'dashboard.widget.department_wise_rating')"
        cols="12"
      >
        <AppLazy :key="unit_id + fromDate + toDate">
          <DepartmentWiseRating
            :from="fromDate"
            :to="toDate"
            :unit-id="unit_id"
          />
        </AppLazy>
      </VCol>

      <!-- Department Monthly Analytics -->
      <VCol
        v-if="can('view', 'dashboard.widget.department_wise_rating')"
        cols="12"
      >
        <AppLazy :key="unit_id + fromDate + toDate">
          <DepartmentMonthlyAnalytics
            :from="fromDate"
            :to="toDate"
            :unit-id="unit_id"
          />
        </AppLazy>
      </VCol>

      <!-- Department Complaint Analytics -->
      <VCol
        v-if="can('view', 'dashboard.widget.department_complaint_analytics')"
        cols="12"
      >
        <AppLazy :key="'complaint' + unit_id + fromDate + toDate">
          <AnalyticsDepartment
            :types="['COMPLAINT']"
            title="Department Complaint Analytics"
            :from="fromDate"
            :to="toDate"
            :unit-id="unit_id"
            :colors="['#FF0000', '#00FF00']"
            @update:ticket-table-filter="onUpdateTicketTableFilter"
          />
        </AppLazy>
      </VCol>

      <!-- Department Compliment Analytics -->
      <VCol
        v-if="can('view', 'dashboard.widget.department_compliment_analytics')"
        cols="12"
      >
        <AppLazy :key="'compliment' + unit_id + fromDate + toDate">
          <AnalyticsDepartment
            :types="['COMPLIMENT']"
            title="Department Compliment Analytics"
            :from="fromDate"
            :to="toDate"
            :unit-id="unit_id"
            :colors="['#00FF00', '#FF0000']"
            @update:ticket-table-filter="onUpdateTicketTableFilter"
          />
        </AppLazy>
      </VCol>

      <!-- Yearly NPS Trend -->
      <VCol
        v-if="can('view', 'dashboard.widget.yearly_nps_trend')"
        cols="12"
        lg="12"
        md="12"
      >
        <AppLazy :key="unit_id + fromDate + toDate">
          <AnalyticsNpsMonthlyTrend
            :from="fromDate"
            :to="toDate"
            :unit-id="unit_id"
          />
        </AppLazy>
      </VCol>

      <!-- Feedback Tracker -->
      <VCol
        v-if="can('view', 'dashboard.widget.feedback_tracker')"
        cols="12"
        lg="6"
        md="6"
      >
        <AppLazy :key="unit_id + fromDate + toDate">
          <DashboardAnalyticsFeedbackTracker
            :from="fromDate"
            :to="toDate"
            :unit-id="unit_id"
            @update:feedback-table-filter="onUpdateFeedbackTableFilter"
          />
        </AppLazy>
      </VCol>

      <!-- Google Review Tracker -->
      <VCol
        v-if="can('view', 'dashboard.widget.google_review_tracker')"
        cols="12"
        lg="6"
        md="6"
      >
        <AppLazy :key="unit_id + fromDate + toDate">
          <AnalyticsGoogleReviewTracker
            :from="fromDate"
            :to="toDate"
            :unit-id="unit_id"
            @update:feedback-table-filter="onUpdateFeedbackTableFilter"
          />
        </AppLazy>
      </VCol>

      <!-- Feedback Form Tracker -->
      <VCol
        v-if="can('view', 'dashboard.widget.feedback_form_tracker')"
        cols="12"
      >
        <AppLazy :key="unit_id + fromDate + toDate">
          <AnalyticsFormTypeTracker
            :from="fromDate"
            :to="toDate"
            :unit-id="unit_id"
            @update:feedback-table-filter="onUpdateFeedbackTableFilter"
          />
        </AppLazy>
      </VCol>

      <!-- Form Type Wise NPS Tracker -->
      <VCol
        v-if="can('view', 'dashboard.widget.form_type_wise_nps_tracker')"
        cols="12"
      >
        <AppLazy :key="unit_id + fromDate + toDate">
          <FormNPSTracker
            :from="fromDate"
            :to="toDate"
            :unit-id="unit_id"
          />
        </AppLazy>
      </VCol>

      <!-- NPS Monthly Analysis -->
      <VCol
        v-if="can('view', 'dashboard.widget.nps_monthly_analysis')"
        cols="12"
      >
        <AppLazy :key="unit_id + fromDate + toDate">
          <NPSMonthlyAnalysis
            :from="fromDate"
            :to="toDate"
            :unit-id="unit_id"
          />
        </AppLazy>
      </VCol>

      <!-- Feedback Source Tracker -->
      <VCol
        v-if="can('view', 'dashboard.widget.feedback_source_tracker')"
        cols="12"
      >
        <AppLazy :key="unit_id + fromDate + toDate">
          <AnalyticsSourceTypeTracker
            :from="fromDate"
            :to="toDate"
            :unit-id="unit_id"
            @update:feedback-table-filter="onUpdateFeedbackTableFilter"
          />
        </AppLazy>
      </VCol>

      <!-- Doctor Wise Feedback -->
      <VCol
        v-if="can('view', 'dashboard.widget.doctor_wise_feedback')"
        cols="12"
      >
        <AppLazy :key="unit_id + fromDate + toDate">
          <AnalyticsEpisodeDoctorWiseFeedback
            title="Doctor Wise Feedback"
            :from="fromDate"
            :to="toDate"
            :unit-id="unit_id"
          />
        </AppLazy>
      </VCol>

      <!-- User Wise Feedback -->
      <VCol
        v-if="can('view', 'dashboard.widget.user_wise_feedback')"
        cols="12"
      >
        <AppLazy :key="unit_id + fromDate + toDate">
          <AnalyticsUserWiseFeedback
            title="User Wise Feedback Analytics"
            :from="fromDate"
            :to="toDate"
            :unit-id="unit_id"
            @update:feedback-table-filter="onUpdateFeedbackTableFilter"
          />
        </AppLazy>
      </VCol>

      <!-- Department CAPA Rating -->
      <VCol
        v-if="can('view', 'dashboard.widget.department_capa_rating')"
        cols="12"
      >
        <AppLazy :key="unit_id + fromDate + toDate">
          <AnalyticsDepartmentCapaRating
            title="Department CAPA Rating"
            :from="fromDate"
            :to="toDate"
            :unit-id="unit_id"
          />
        </AppLazy>
      </VCol>
    </VRow>
  </div>

  <!-- Feedback Table Dialog -->
  <VDialog v-model="isFeedbackTableDialogVisible">
    <!-- 👉 dialog close btn -->
    <DialogCloseBtn
      size="small"
      @click="() => (isFeedbackTableDialogVisible = !isFeedbackTableDialogVisible)"
    />

    <VCard>
      <VCardText>
        <FeedbackTable
          :feedback-filters="feedbackTableFilter"
          :action-visible="false"
          :style="{
            cardVariant: 'flat'
          }"
        />
      </VCardText>
    </VCard>
  </VDialog>

  <!-- Table Dialog -->
  <VDialog v-model="isTicketTableDialogVisible">
    <!-- 👉 dialog close btn -->
    <DialogCloseBtn
      size="small"
      @click="() => (isTicketTableDialogVisible = !isTicketTableDialogVisible)"
    />

    <VCard>
      <VCardText>
        <TicketTable
          ref="refTicketTable"
          :page="ticketType == 'COMPLAINT' ? 'ticket-management' : 'compliment'"
          :ticket-filters="ticketTableFilter"
          :action-visible="false"
          :style="{
            cardVariant: 'flat'
          }"
        />
      </VCardText>
    </VCard>
  </VDialog>
</template>

